import { OnChangeFn } from '@tanstack/react-table';
import { PillModifier } from 'components/pill';
import { TableDataRes } from 'components/table/base/tableBaseApi';
import { MaterialReactTable, useMaterialReactTable,
  type MRT_RowSelectionState,
} from 'material-react-table';

export interface ValidationDetail {
  is_completed: boolean;
  missing_transaction_result: MissingTransactionResult;
  total: ValidationTotalResult;
  flow_negatives: FlowNegativeItem[] | null;
  match_cache_date: number | null | undefined;
}

export interface MissingTransactionResult {
  exact: number;
  high: number;
  low: number;
  medium: number;
  noMatch: number;
  veryLow: number;
}

export interface ValidationTotalResult {
  duplications: number;
  flow_negatives: number;
  missing_deposits: number;
  missing_withdrawals: number;
  validation_notices: number;
  validation_warnings: number;
}
export interface MatchElement {
  color: PillModifier[];
  rowId: number;
  transactionId: number;
  coin: string | undefined;
  transaction_time: number | undefined;
}

export interface FullMatchElement {
  match: MatchElement;
  transaction: ValidationTableDef;
}

export interface ValidationTableTransaction {
  id: number | null;
  transaction_type: number | null;
  buy: number | null;
  buy_value_in_fiat: number | null;
  sell: number | null;
  sell_value_in_fiat: number | null;
  fee: number | null;
  sell_coin: string | null;
  buy_coin: string | null;
  fee_coin: string | null;
  exchange: string | null;// CSV2
  trade_group: string | null;
  trade_hash: string | null;
  comment: string | null;
  transaction_time: number | null;
  matched_transaction_id: number | null;
  is_strict_match: boolean | null;
  is_auto_match_blocked: boolean | null;
  trade_id: string | null;
}

export interface ValidationTableReport {
  status: number;
  count: number;
  is_duplicate: boolean;
  warnings: ValidationTableWarnings;
  notices: ValidationTableNotices;
}

export interface ValidationTableWarnings {
  is_transaction_type_incorrect: boolean;
  is_buy_amount_negative_or_exceeding_the_limit: boolean;
  is_buy_coin_not_set: boolean;
  is_buy_coin_contains_not_allowed_characters: boolean;
  is_incoming_transaction_also_contains_outgoing_values: boolean;
  is_sell_amount_negative_or_exceeding_the_limit: boolean;
  is_sell_coin_not_set: boolean;
  is_sell_coin_contains_not_allowed_characters: boolean;
  is_outgoing_transaction_also_contains_incoming_values: boolean;
  is_fee_amount_negative_or_exceeding_the_limit: boolean;
  is_fee_coin_not_set: boolean;
  is_fee_coin_contains_not_allowed_characters: boolean;
  is_transaction_date_before_the_limit: boolean;
  is_transaction_date_more_than_thirty_days_in_the_future: boolean;
  is_there_white_space_at_the_beginning_of_exchange_name: boolean;
  is_there_white_space_at_the_end_of_exchange_name: boolean;
  is_there_white_space_at_the_beginning_of_trade_group: boolean;
  is_there_white_space_at_the_end_of_trade_group: boolean;
  is_there_white_space_at_the_beginning_of_comment: boolean;
  is_there_white_space_at_the_end_of_comment: boolean;
}

export interface ValidationTableNotices {
  is_asset_values_not_matching: boolean;
  is_asset_value_missing: boolean;
}

export interface Dictionary<T> {
  [Key: string]: T;
}

export interface ValidationTableFlow {
  before: number | null;
  after: number | null;
  coin: string | null;
}

export interface ValidationTableMatchResult {
  difference: number | null;
  match_possibility: number | null;
  transaction_id: number | null;

  //old match
  info: ValidationTableDef | null;//some endpoints return info

  //new match
  transaction: ValidationTableTransaction | null;//some endpoints return info
  report: ValidationTableReport | null;
  account_balance: AccountBalance | null;
  match_result: ValidationTableMatchResult | null;// TODO: remove or is it still there?
  possible_matches: ValidationTableMatchResult[] | null;

  matched_transaction: ValidationTableDef | null;
}

export interface ValidationTableDef {
  transaction: ValidationTableTransaction;
  report: ValidationTableReport | null;
  account_balance: AccountBalance | null;
  match_result: ValidationTableMatchResult;// TODO: remove or is it still there?
  possible_matches: ValidationTableMatchResult[];

  matched_transaction: ValidationTableTransaction | null;
  // This is only recursive once, field is null after that
}

export interface AccountBalance {
  general: ValidationTableFlow[] | null;
  exchange: Dictionary<ValidationTableFlow[]> | null;
}

export interface CoinValue {
  name: string | null;
  value: number | null;
  fiatValue: number | null;
  exchange: string | null;
}

export interface ExchangeValue {
  exchange1: string | null;
  exchange2: string | null;
}

export interface ValidationSelectOption {
  text: string;
  value: number;
}

export enum ValidationSectionId {
  //having 0 would bug due to being falsy in js
  NEGATIVE = 1,
  DEPOSITWITHDRAWAL = 2,
  NOTICE = 3,
  WARNING = 4,
  DUPLICATE = 5,
}

export interface ValidationEditItem {
  transaction_type?: string | undefined;
  comment?: string | undefined;
  buy?: string | undefined;
  buy_coin?: string | undefined;
  buy_value_in_fiat?: string | undefined;
  sell?: string | undefined;
  sell_coin?: string | undefined;
  sell_value_in_fiat?: string | undefined;
  fee?: string | undefined;
  fee_coin?: string | undefined;
  exchange?: string | undefined;
  transaction_time?: number | undefined;
  trade_id?: string | undefined;//can be either trade_id or trade_hash
}

export interface ValidationMaterialTableProps { // implements TableWrapperProps
  // onErrorCB?: (e: unknown) => void;
  isFetching: boolean;
  setIsFetching: (value: boolean) => void;

  statusSelectOption: ValidationSelectOption[];
  transactionTypeSelectOption: ValidationSelectOption[];

  globalSearch: string|null;

  rowSelection: MRT_RowSelectionState;
  setRowSelection: OnChangeFn<MRT_RowSelectionState>; 
  
  onLoadCb: (detail: TableDataRes<ValidationTableDef, ValidationDetail> | null) => void;
}

export interface ValidationMaterialTableMatchProps extends ValidationMaterialTableProps {
  alreadyMatchedOnly: boolean;
}

export enum GeneralTransactionType {
  IN = 1,
  OUT = 2,
  TRANSFER = 3,
  OTHER = 4,
}

export interface GeneralTransactionTypeModel {
  value: number;
  in: boolean;
  out: boolean;
}

export const generalTransactionTypes: GeneralTransactionTypeModel[] = [{
  value: 0,  "in": true, "out": true,
},{
  value: 102,  "in": true, "out": true,
},{
value: 1,  "in": true, "out": false,
},{
value: 3,  "in": true, "out": false,
},{
value: 5,  "in": true, "out": false,
},{
value: 57,  "in": true, "out": false,
},{
value: 51,  "in": true, "out": false,
},{
value: 4,  "in": true, "out": false,
},{
value: 52,  "in": true, "out": false,
},{
value: 58,  "in": true, "out": false,
},{
value: 50,  "in": true, "out": false,
},{
value: 53,  "in": true, "out": false,
},{
value: 61,  "in": true, "out": false,
},{
value: 54,  "in": true, "out": false,
},{
value: 55,  "in": true, "out": false,
},{
value: 56,  "in": true, "out": false,
},{
value: 59,  "in": true, "out": false,
},{
value: 60,  "in": true, "out": false,
},{
value: 90,  "in": true, "out": false,
},{
value: 93,  "in": true, "out": false,
},{
value: 94,  "in": true, "out": false,
},{
value: 95,  "in": true, "out": false,
},{
value: 13,  "in": false, "out": true,
},{
value: 91,  "in": true, "out": false,
},{
value: 97,  "in": true, "out": false,
},{
value: 98,  "in": true, "out": false,
},{
value: 2,  "in": false, "out": true,
},{
value: 6,  "in": false, "out": true,
},{
value: 7,  "in": false, "out": true,
},{
value: 8,  "in": false, "out": true,
},{
value: 9,  "in": false, "out": true,
},{
value: 10,  "in": false, "out": true,
},{
value: 11,  "in": false, "out": true,
},{
value: 12,  "in": false, "out": true,
},{
value: 15,  "in": false, "out": true,
},{
value: 14,  "in": false, "out": true,
},{
value: 16,  "in": false, "out": true,
},{
value: 40,  "in": false, "out": true,
},{
value: 17,  "in": false, "out": true,
},{
value: 18,  "in": false, "out": true,
},{
value: 41,  "in": false, "out": true,
},{
value: 42,  "in": false, "out": true,
},{
value: 43,  "in": false, "out": true,
},{
value: 44,  "in": false, "out": true,
},{
value: 100,  "in": true, "out": true,
},{
value: 101,  "in": true, "out": true,
},{
value: 999,  "in": true, "out": true,
}];

export interface FlowNegativeItem {
  balance_after: number | null;
  balance_before: number | null;

  previous_balance_after: number | null;
  previous_balance_before: number | null;

  //only for subrows
  after: number | null;
  before: number | null;

  coin: string | null;
  exchange: string | null;
  is_first_negative: boolean | undefined;//only for subrows
  
  transaction: ValidationTableTransaction;
  previous_transaction: ValidationTableTransaction | null;//needs to be null for subrows

  //subrows
  before_negative: FlowNegativeItem[] | null;
  after_negative: FlowNegativeItem[] | null;
  //fake row for parent copy
  is_parent_copy: boolean | undefined;
}

export interface ExchangeValidationFilter {
  data: string[] | null;
  status: number;
}